import React, { useEffect, useState } from 'react';
import { Col, Descriptions, Drawer, Layout, Menu, Modal, Row, Spin, Switch, Tag, Tooltip } from 'antd';
import {
    HomeOutlined,
    LogoutOutlined,
    UserOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styles from './Sidebar.module.scss';
import { getModulesCookie, getRolCookie, getValidationMainModules, logout } from '../../../services/auth';
import { AppState } from '../../../store';
import { ProfileResponse, UserActionRequest } from '../../../store/user/types';
import { getModules, getProfile } from '../../../store/user/thunks';
import useMediaQuery from '../hooks/useMediaQuery';
import iconMap from './iconMapping';
import { getThemePreference, setThemePreference, ThemeMode } from '../../../services/themePreference';

const { Content, Sider } = Layout;
const LOGO_URL = 'https://repositorysmilemed.blob.core.windows.net/images/logos/sinfondosolologotbc.png';

interface UserProps {
    children: any;
    userAction: UserActionRequest;
    getModules?: any;
    getProfile?: any;
}

interface MenuItem {
    sequential: string;
    label: string;
    url: string;
    icon?: string;
    visible: boolean;
    keySubModule: string;
}

export interface Module {
    key: number;
    id: string;
    keyModule: string;
    keySubModule?: string;
    url: string;
    label: string;
    status: string;
    icon: string;
    visible: boolean;
    createdAt: string;
    items?: MenuItem[];
}

const SideBar: React.FC<UserProps> = (props: UserProps) => {
    const history = useHistory();
    const location = useLocation();
    const isNominaPortal = location.pathname.startsWith('/nomina-portal');
    const NominaIcon = iconMap['checkin'];
    const { getModules: loadModules } = props;

    const [dataTableModule, setDataTableModule] = useState<Module[]>([]);
    const [isValidationMainModules, setIsValidationMainModules] = useState<string | null>('');
    const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false);
    const [isProfileLoading, setIsProfileLoading] = useState<boolean>(false);
    const [profile, setProfile] = useState<ProfileResponse | null>(null);
    const [themeMode, setThemeMode] = useState<ThemeMode>(getThemePreference());
    const [mobileDrawerModule, setMobileDrawerModule] = useState<Module | null>(null);
    const collapsed = true;

    const matches = useMediaQuery('(max-width: 768px)');

    useEffect(() => {
        const init = async () => {
            const modules = await loadModules(getModulesCookie(), getRolCookie());
            setDataTableModule(modules || []);
            setIsValidationMainModules(getValidationMainModules());
        };

        if (isNominaPortal) {
            setIsValidationMainModules(getValidationMainModules());
            return;
        }

        init();
    }, [isNominaPortal, loadModules]);

    const onLogout = async () => {
        logout(process.env.REACT_APP_BASE_URL as string);
    };

    const goHome = async () => {
        history.push('/main-modules');
    };

    const goToModule = (module: Module) => {
        if (module.items && module.items.length > 0) {
            setMobileDrawerModule(module);
            return;
        }

        history.push(`/${module.url}`);
    };

    const goToSubModule = (url: string) => {
        setMobileDrawerModule(null);
        history.push(`/${url}`);
    };

    const openProfile = async () => {
        setIsProfileOpen(true);
        setIsProfileLoading(true);
        const response = await props.getProfile();
        setProfile(response || null);
        setIsProfileLoading(false);
    };

    const closeProfile = () => {
        setIsProfileOpen(false);
    };

    const resolveProfileValue = (...keys: string[]) => {
        const sources = [
            profile,
            profile?.user,
            profile?.userResponse,
            profile?.userInformationResponse,
            profile?.organization,
            profile?.organizationResponse,
            profile?.contract,
            profile?.contractResponse,
            profile?.plan,
            profile?.planResponse,
            profile?.role,
            profile?.rol,
            profile?.rolResponse,
        ];

        for (const source of sources) {
            if (!source) continue;

            for (const key of keys) {
                const value = source[key];
                if (value !== undefined && value !== null && value !== '') {
                    return value;
                }
            }
        }

        return null;
    };

    const getProfileName = () => {
        const fullName = resolveProfileValue('fullName', 'name', 'userName');
        if (fullName) return fullName;

        const firstName = resolveProfileValue('firstName');
        const lastName = resolveProfileValue('lastName');
        return [firstName, lastName].filter(Boolean).join(' ') || 'Usuario';
    };

    const renderProfileValue = (...keys: string[]) => resolveProfileValue(...keys) || 'No registrado';

    const handleThemeChange = (checked: boolean) => {
        const nextTheme = checked ? 'dark' : 'light';
        setThemeMode(nextTheme);
        setThemePreference(nextTheme);
    };

    const getProfileRoles = () => {
        const roles = profile?.role || profile?.roles || profile?.rolSystems || [];
        if (!Array.isArray(roles) || roles.length === 0) return null;

        return roles
            .map((role: any) => {
                const roleName = role.rolName || role.roleName || role.name;
                const systemName = role.systemName || role.systemLabel;

                if (roleName && systemName) return `${roleName} - ${systemName}`;
                return roleName || systemName;
            })
            .filter(Boolean);
    };

    const renderMenuLabel = (IconComponent: any, label: string, submenu = false) => (
        <div className={submenu ? styles['layout-admin-submenu-item-text'] : styles['layout-admin-text']}>
            {IconComponent && <IconComponent />}
            <span>{label}</span>
        </div>
    );

    const renderQuickAction = (
        key: string,
        icon: React.ReactNode,
        label: string,
        onClick: () => void,
        hidden = false
    ) => {
        if (hidden) return null;

        const action = (
            <button type="button" onClick={onClick} className={styles['layout-admin-action']}>
                {icon}
                <span>{label}</span>
            </button>
        );

        return collapsed ? (
            <Tooltip key={key} placement="right" title={label}>
                {action}
            </Tooltip>
        ) : (
            <React.Fragment key={key}>{action}</React.Fragment>
        );
    };

    const renderMobileModuleButton = (singleOption: Module) => {
        const IconComponent = iconMap[singleOption.icon] || iconMap.default;
        const moduleKey = singleOption.keySubModule || singleOption.id || singleOption.keyModule || `${singleOption.key}`;
        const mobileLabel = singleOption.label
            .replace(/^Facturacion de planes$/i, 'Contratos')
            .replace(/^Gestion de contratos$/i, 'Contratos')
            .replace(/^Gestion de planes$/i, 'Planes')
            .replace(/^Plan management$/i, 'Planes')
            .replace(/^Plan billing$/i, 'Contratos');

        if (!singleOption.visible && (!singleOption.items || singleOption.items.length === 0)) {
            return null;
        }

        return (
            <button
                type="button"
                key={moduleKey}
                className={styles['mobile-nav-item']}
                onClick={() => goToModule(singleOption)}
            >
                {IconComponent && <IconComponent />}
                <span>{mobileLabel}</span>
            </button>
        );
    };

    return (
        <div className={styles['layout-admin-container']}>
            <Layout>
                {!matches && (
                    <Sider
                        collapsed={collapsed}
                        collapsedWidth={84}
                        width={84}
                        className={styles['layout-admin-sider']}
                    >
                        <div className={styles['layout-admin-logo']}>
                            <img loading="lazy" src={LOGO_URL} alt="The Blue Coding" />
                        </div>

                        <Menu className={styles['layout-admin-menu']} mode="inline">
                            {isNominaPortal ? (
                                <Menu.Item key="nomina" title="Nomina">
                                    {renderMenuLabel(NominaIcon, 'Nomina')}
                                </Menu.Item>
                            ) : (
                                <>
                                    {dataTableModule.map((singleOption: Module) => {
                                        const IconComponent = iconMap[singleOption.icon] || iconMap.default;
                                        const moduleKey =
                                            singleOption.keySubModule || singleOption.id || singleOption.keyModule || `${singleOption.key}`;

                                        if (!IconComponent) {
                                            console.warn(`Icon "${singleOption.icon}" not found in iconMap`);
                                        }

                                        if (singleOption.items && singleOption.items.length > 0) {
                                            return (
                                                <Menu.SubMenu
                                                    key={moduleKey}
                                                    title={renderMenuLabel(IconComponent, singleOption.label)}
                                                >
                                                    {singleOption.items.map((child: MenuItem) => {
                                                        const ChildIconComponent = child.icon ? iconMap[child.icon] : null;

                                                        return (
                                                            <Menu.Item
                                                                key={child.keySubModule || child.sequential || child.url}
                                                                onClick={() => history.push(`/${child.url}`)}
                                                                title={child.label}
                                                            >
                                                                {renderMenuLabel(ChildIconComponent, child.label, true)}
                                                            </Menu.Item>
                                                        );
                                                    })}
                                                </Menu.SubMenu>
                                            );
                                        }

                                        if (!singleOption.visible) return null;

                                        return (
                                            <Menu.Item
                                                key={moduleKey}
                                                onClick={() => history.push(`/${singleOption.url}`)}
                                                title={singleOption.label}
                                            >
                                                {renderMenuLabel(IconComponent, singleOption.label)}
                                            </Menu.Item>
                                        );
                                    })}
                                </>
                            )}
                        </Menu>

                        <div className={styles['layout-admin-actions']}>
                            {renderQuickAction('home', <HomeOutlined />, 'Modulos', goHome, isValidationMainModules !== 'SI')}
                            {renderQuickAction('profile', <UserOutlined />, 'Mi perfil', openProfile)}
                            {renderQuickAction('logout', <LogoutOutlined />, 'Cerrar sesion', onLogout)}
                        </div>
                    </Sider>
                )}

                <Layout>
                    <Content className={styles['layout-admin-content']}>
                        {props.children}
                    </Content>
                    <Row justify="end">
                        <Col>
                            <div>{process.env.REACT_APP_VERSION}</div>
                        </Col>
                    </Row>
                </Layout>
            </Layout>

            {matches && (
                <nav className={styles['mobile-bottom-nav']}>
                    <div className={styles['mobile-bottom-scroll']}>
                        {isValidationMainModules === 'SI' && (
                            <button type="button" className={styles['mobile-nav-item']} onClick={goHome}>
                                <HomeOutlined />
                                <span>Modulos</span>
                            </button>
                        )}

                        {isNominaPortal ? (
                            <button type="button" className={styles['mobile-nav-item']}>
                                {NominaIcon && <NominaIcon />}
                                <span>Nomina</span>
                            </button>
                        ) : (
                            dataTableModule.map(renderMobileModuleButton)
                        )}

                        <button type="button" className={styles['mobile-nav-item']} onClick={openProfile}>
                            <UserOutlined />
                            <span>Perfil</span>
                        </button>
                        <button type="button" className={styles['mobile-nav-item']} onClick={onLogout}>
                            <LogoutOutlined />
                            <span>Salir</span>
                        </button>
                    </div>
                </nav>
            )}

            <Drawer
                open={!!mobileDrawerModule}
                placement="bottom"
                height="auto"
                title={mobileDrawerModule?.label}
                onClose={() => setMobileDrawerModule(null)}
                className={styles['mobile-module-drawer']}
            >
                <div className={styles['mobile-module-list']}>
                    {mobileDrawerModule?.items?.map((child: MenuItem) => {
                        const ChildIconComponent = child.icon ? iconMap[child.icon] : null;

                        return (
                            <button
                                type="button"
                                key={child.keySubModule || child.sequential || child.url}
                                className={styles['mobile-module-option']}
                                onClick={() => goToSubModule(child.url)}
                            >
                                {ChildIconComponent && <ChildIconComponent />}
                                <span>{child.label}</span>
                            </button>
                        );
                    })}
                </div>
            </Drawer>

            <Modal
                open={isProfileOpen}
                title="Mi perfil"
                footer={null}
                onCancel={closeProfile}
                width={560}
                destroyOnClose
            >
                {isProfileLoading ? (
                    <div className={styles['profile-loading']}>
                        <Spin />
                    </div>
                ) : (
                    <div className={styles['profile-modal']}>
                        <div className={styles['profile-header']}>
                            <div className={styles['profile-avatar']}>
                                {String(getProfileName()).charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h3>{getProfileName()}</h3>
                                <p>{renderProfileValue('email')}</p>
                            </div>
                        </div>

                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Tema oscuro">
                                <Switch
                                    checked={themeMode === 'dark'}
                                    checkedChildren="ON"
                                    unCheckedChildren="OFF"
                                    onChange={handleThemeChange}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Identificacion">
                                {renderProfileValue('identification', 'dni', 'document')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Rol">
                                {getProfileRoles()?.map((role: string) => (
                                    <Tag color="blue" key={role}>{role}</Tag>
                                )) || <Tag color="blue">{renderProfileValue('rolName', 'roleName', 'groupName')}</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="Organizacion">
                                {renderProfileValue('organizationName', 'organization', 'tenantName')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Estado">
                                {renderProfileValue('status')}
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Modal>
        </div>
    );
};

const mapStateToProps = (state: AppState) => ({
    userAction: state.userAction
});

export default connect(mapStateToProps, {
    getModules,
    getProfile
})(SideBar);
